import { appLinks } from "@vendata/power-up-links";
import type { Repair } from "@vendata/power-up-spec/schemas/Repair";

export const REPAIR_TABLE_COLUMN_STORAGE_KEY = "power-up.repair.columns";

/** Path to a user-facing repair detail page. */
export function repairDetailPath(id: string): string {
  return appLinks.repairDetail.path.replace(":id", id);
}

/** Formats an ISO timestamp for repair list rows. */
export function formatRepairCreatedAt(iso: string | null | undefined): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString();
}

export interface RepairTableColumnDef {
  key: keyof Repair | "id";
  title: string;
  defaultVisible: boolean;
  sortable: boolean;
  align?: "end";
}

/** Fixed column order. Defaults match the original request list. */
export function buildRepairTableColumns(
  titles: Record<string, string>,
): RepairTableColumnDef[] {
  return [
    { key: "part_to_repair", title: titles.col_part, defaultVisible: true, sortable: true },
    { key: "work_order", title: titles.col_work_order, defaultVisible: true, sortable: true },
    { key: "repair_shop_name", title: titles.col_shop, defaultVisible: true, sortable: true },
    { key: "created_at", title: titles.col_opened, defaultVisible: true, sortable: true },
    {
      key: "repair_cost_approx",
      title: titles.col_cost,
      defaultVisible: true,
      sortable: true,
      align: "end",
    },
    { key: "status", title: titles.col_status, defaultVisible: false, sortable: true },
    { key: "notes", title: titles.col_notes, defaultVisible: false, sortable: false },
    { key: "closed_at", title: titles.col_closed, defaultVisible: false, sortable: true },
    { key: "updated_at", title: titles.col_updated, defaultVisible: false, sortable: true },
    { key: "org_id", title: titles.col_org, defaultVisible: false, sortable: true },
    {
      key: "created_by_user_id",
      title: titles.col_submitted_by,
      defaultVisible: false,
      sortable: false,
    },
    { key: "id", title: titles.col_id, defaultVisible: false, sortable: true },
  ];
}

export function repairHeadersForKeys(
  columns: RepairTableColumnDef[],
  visibleKeys: readonly string[],
) {
  const visible = new Set(visibleKeys);
  return columns
    .filter((column) => visible.has(column.key))
    .map((column) => ({
      title: column.title,
      key: column.key,
      sortable: column.sortable,
      align: column.align,
    }));
}

export function formatPersonName(
  name: { first: string; last: string } | null | undefined,
  emptyLabel: string,
): string {
  if (!name) return emptyLabel;
  const text = `${name.first} ${name.last}`.trim();
  return text || emptyLabel;
}

export function formatRepairCell(
  repair: Repair,
  key: string,
  emptyLabel: string,
  statusLabels: { open: string; closed: string },
): string {
  switch (key) {
    case "created_at":
    case "updated_at":
    case "closed_at":
      return formatRepairCreatedAt(repair[key]) || emptyLabel;
    case "status":
      return repair.status === "open" ? statusLabels.open : statusLabels.closed;
    case "notes":
      return repair.notes ?? emptyLabel;
    default: {
      const value = repair[key as keyof Repair];
      if (value == null || value === "") return emptyLabel;
      return String(value);
    }
  }
}
